import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { AuthService } from "../../core/auth/auth.service";
import { Firestore, collection, doc, getDoc, setDoc, updateDoc, deleteDoc, DocumentReference, onSnapshot } from '@angular/fire/firestore';
import { distinctUntilChanged } from "rxjs/operators";
import { EntityCategory } from './entitycategories.types';
import { User } from 'firebase/auth';

@Injectable({
    providedIn: 'root'
})
export class EntitycategoriesService {
    private _authService: AuthService = inject(AuthService);
    private _firestore: Firestore = inject(Firestore);
    private _injector = inject(EnvironmentInjector);


    private _entitycategoriesSubject: BehaviorSubject<EntityCategory[] | null> = new BehaviorSubject(null);
    entitycategories$: Observable<EntityCategory[]> = this._entitycategoriesSubject.asObservable();

    private _entityCategorySubject: BehaviorSubject<EntityCategory | null> = new BehaviorSubject(null);
    entitycategory$: Observable<EntityCategory> = this._entityCategorySubject.asObservable();

    private unsubscribeSnapshot?: () => void;
    private unsubscribeSingleCategorySnapshot?: () => void;
    private authContextSubscription?: Subscription;
    private currentEntityCategoryId: string;

    constructor() {
        this.initializeAuthListeners();
    }

    private initializeAuthListeners(): void {
        this.initializeAuthContextListener();

        this._authService.onSignOut.subscribe(() => {
            this.handleSignOut();
        });
    }

    private initializeAuthContextListener(): void {
        this.authContextSubscription?.unsubscribe();

        this.authContextSubscription = combineLatest([
            this._authService.orgId$,
            this._authService.firebaseUser$
        ])
            .pipe(
                distinctUntilChanged(([previousOrgId, previousUser], [nextOrgId, nextUser]) =>
                    previousOrgId === nextOrgId && previousUser?.uid === nextUser?.uid
                )
            )
            .subscribe(([orgId, firebaseUser]) => {
                this.handleAuthContextChange(orgId, firebaseUser);
            });
    }

    private handleAuthContextChange(orgId: string | null, firebaseUser: User | null): void {
        this.cleanup();

        if (!orgId || !firebaseUser) {
            this._entitycategoriesSubject.next(null);
            this._entityCategorySubject.next(null);
            return;
        }

        this.subscribeToEntityCategories(orgId);
        if (this.currentEntityCategoryId) {
            this.setCurrentEntitycategoryId(this.currentEntityCategoryId);
        }
    }

    private subscribeToEntityCategories(orgId: string): void {
        runInInjectionContext(this._injector, () => {
            const ref = collection(this._firestore, `organisations/${orgId}/entitycategories/`);

            this.unsubscribeSnapshot = onSnapshot(
                ref,
                (snapshot) => this.handleEntityCategoriesSnapshot(snapshot),
                (error) => this.handleEntityCategoriesError(error)
            );
        });
    }

    private handleEntityCategoriesSnapshot(snapshot: any): void {
        const entitycategories: EntityCategory[] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as EntityCategory[];

        this._entitycategoriesSubject.next(entitycategories);
    }

    private handleEntityCategoriesError(error: any): void {
        console.error("Entity Categories Sync Error:", error);
        this._entitycategoriesSubject.next(null);
    }

    get currentEntityCategories(): EntityCategory[] | null {
        return this._entitycategoriesSubject.getValue();
    }

    async setCurrentEntitycategoryId(entityCategoryId: string): Promise<void> {
        this.currentEntityCategoryId = entityCategoryId;
        runInInjectionContext(this._injector, () => {
            const ref = doc(this._firestore, `organisations/${this._authService.getOrganisationId()}/entitycategories/`, entityCategoryId);

            this.unsubscribeSingleCategorySnapshot = onSnapshot(
                ref,
                (snapshot) => this.handleEntityCategorySnapshot(snapshot),
                (error) => this.handleEntityCategoryError(error)
            );
        });
    }

    private handleEntityCategorySnapshot(snapshot: any): void {
        if (snapshot.exists()) {
            this._entityCategorySubject.next({ id: snapshot.id, ...snapshot.data() } as EntityCategory);
        } else {
            this._entityCategorySubject.next(null);
        }
    }

    private handleEntityCategoryError(error: any): void {
        console.error("Entity Category Sync Error:", error);
        this._entityCategorySubject.next(null);
    }

    async addEntityCategory(entityCategory: EntityCategory): Promise<{ success: boolean, errorMessage?: string }> {
        const docRef = doc(this._firestore, `organisations/${this._authService.getOrganisationId()}/entitycategories/`, entityCategory.id) as DocumentReference<EntityCategory>;
        const existingDoc = await getDoc(docRef);
        if (existingDoc.exists()) {
            return { success: false, errorMessage: 'There was an error when creating the entity category. A category with that ID already exists.' };
        }
        try {
            await setDoc(docRef, entityCategory);
            return { success: true };
        } catch (error) {
            return { success: false };
        }
    }

    async updateEntityCategory(entityCategory: EntityCategory): Promise<void> {
        const docRef = doc(this._firestore, `organisations/${this._authService.getOrganisationId()}/entitycategories/`, entityCategory.id);
        await updateDoc(docRef, { ...entityCategory } as any);
    }

    async deleteEntityCategory(entityId: string): Promise<void> {
        const docRef = doc(this._firestore, `organisations/${this._authService.getOrganisationId()}/entitycategories/`, entityId);
        await deleteDoc(docRef);
    }

    private handleSignOut(): void {
        this.currentEntityCategoryId = null;
        this._entitycategoriesSubject.next(null);
        this._entityCategorySubject.next(null);
        this.cleanup();
    }

    private cleanup(): void {
        if (this.unsubscribeSnapshot) {
            this.unsubscribeSnapshot();
            this.unsubscribeSnapshot = undefined;
        }

        if (this.unsubscribeSingleCategorySnapshot) {
            this.unsubscribeSingleCategorySnapshot();
            this.unsubscribeSingleCategorySnapshot = undefined;
        }
    }

    ngOnDestroy(): void {
        this.authContextSubscription?.unsubscribe();
        this.cleanup();
    }
}
